import React, { createContext, useContext, useState, useEffect } from 'react';
import { onAuthStateChanged } from 'firebase/auth';
import { auth } from '../config/firebase';
import { authService } from '../services/authService';

const AuthContext = createContext(null);

// Cache helpers for instant UI on navigation
const CACHE_KEY = 'rgnr_auth_cache';

const getCachedAuth = () => {
  try {
    const cached = sessionStorage.getItem(CACHE_KEY);
    return cached ? JSON.parse(cached) : null;
  } catch {
    return null;
  }
};

const setCachedAuth = (userData) => {
  try {
    sessionStorage.setItem(CACHE_KEY, JSON.stringify(userData));
  } catch {
    // Ignore storage errors
  }
};

const clearCachedAuth = () => {
  try {
    sessionStorage.removeItem(CACHE_KEY);
  } catch {
    // Ignore storage errors
  }
};

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [userData, setUserData] = useState(null);
  // Always start loading - wait for Firebase Auth to resolve
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  // Listen to Firebase auth state changes
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (firebaseUser) => {
      if (firebaseUser) {
        // Check cache first - skip Firestore fetch if valid
        const cached = getCachedAuth();
        if (cached && cached.id === firebaseUser.email.toLowerCase()) {
          setUser(firebaseUser);
          setUserData(cached);
          setIsLoading(false);
          
          // Validate in background (don't await)
          authService.getUserData(firebaseUser.email).then(data => {
            if (data && data.allowed) {
              setUserData(data);
              setCachedAuth(data);
            } else {
              // No longer allowed - sign out
              authService.signOut();
              setUser(null);
              setUserData(null);
              clearCachedAuth();
              setError('Access denied. Your email is not authorized to access this application.');
            }
          });
          return; // Early return - don't block on Firestore
        }
        
        // No valid cache - fetch from Firestore (blocking)
        const data = await authService.getUserData(firebaseUser.email);
        
        if (data && data.allowed) {
          setUser(firebaseUser);
          setUserData(data);
          setCachedAuth(data);
        } else {
          // User signed in but not in allowlist - sign them out
          await authService.signOut();
          setUser(null);
          setUserData(null);
          clearCachedAuth();
          setError('Access denied. Your email is not authorized to access this application.');
        }
      } else {
        // User is signed out
        setUser(null);
        setUserData(null);
        clearCachedAuth();
      }
      setIsLoading(false);
    });

    return () => unsubscribe();
  }, []);

  const login = async () => {
    setError(null);
    try {
      const result = await authService.signInWithGoogle();
      setUser(result.user);
      setUserData(result.userData);
      setCachedAuth(result.userData);
    } catch (err) {
      setError(err.message);
    }
  };

  const logout = async () => {
    try {
      clearCachedAuth();
      await authService.signOut();
      setUser(null);
      setUserData(null);
    } catch (err) {
      console.error('Logout error:', err);
    }
  };

  const value = {
    // User info
    user: user ? {
      email: user.email,
      name: user.displayName || userData?.name || user.email,
      picture: user.photoURL,
      ...userData
    } : null,
    
    // Auth state
    isAuthenticated: !!user && !!userData,
    isLoading,
    error,
    
    // Convenience flags from userData
    isFundAdmin: userData?.is_fund_admin === true,
    
    // Actions
    login,
    logout
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}

export default AuthContext;
